// @catalog F.030
// UnitsSparkline — Barras verticales pequeñas por mes mostrando unidades
// vendidas. Diferencia Odoo 16 (gris) vs Odoo 18 (teal) visualmente,
// reemplazando el alert+ReferenceLine del dialog viejo.

import { Box, Stack, Tooltip, Typography } from "@mui/material";
import type { MonthlySale } from "../productDetailTypes";
import { formatMonthLabel } from "./helpers";

interface UnitsSparklineProps {
  monthlySales: MonthlySale[];
  height?: number;
}

export function UnitsSparkline({
  monthlySales,
  height = 60,
}: UnitsSparklineProps) {
  if (!monthlySales.length) {
    return (
      <Box
        sx={{
          color: "var(--ink-3)",
          fontSize: 12.5,
          py: 1,
        }}
      >
        Sin unidades vendidas.
      </Box>
    );
  }
  const units = monthlySales.map((m) => m.unitsSold);
  const max = Math.max(...units, 1);

  const first = monthlySales[0];
  const last = monthlySales[monthlySales.length - 1];

  return (
    <Stack spacing={0.75}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: "3px",
          height,
        }}
      >
        {monthlySales.map((m, i) => {
          const u = units[i] ?? 0;
          const pct = max > 0 ? (u / max) * 100 : 0;
          const isOdoo18 = m.dataSource === "odoo18";
          const bg =
            u === 0
              ? "var(--bg-2)"
              : isOdoo18
                ? "var(--accent)"
                : "var(--ink-3)";
          return (
            <Tooltip
              key={m.monthKey}
              title={`${formatMonthLabel(m.monthKey)} · ${u} pz · ${isOdoo18 ? "Odoo 18" : "Odoo 16"}`}
              arrow
              placement="top"
            >
              <Box
                sx={{
                  flex: 1,
                  height: `${pct}%`,
                  minHeight: u > 0 ? "2px" : "1px",
                  bgcolor: bg,
                  borderRadius: "2px",
                  opacity: u === 0 ? 0.5 : 1,
                  cursor: "default",
                  transition: "opacity 0.15s",
                  "&:hover": { opacity: 0.85 },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ fontSize: 11, color: "var(--ink-4)" }}
      >
        <Typography component="span" sx={{ fontSize: 11 }}>
          {first ? formatMonthLabel(first.monthKey) : ""}
        </Typography>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                bgcolor: "var(--ink-3)",
              }}
            />
            <Typography
              component="span"
              sx={{ fontSize: 11, color: "var(--ink-3)" }}
            >
              Odoo 16
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                bgcolor: "var(--accent)",
              }}
            />
            <Typography
              component="span"
              sx={{ fontSize: 11, color: "var(--ink-3)" }}
            >
              Odoo 18
            </Typography>
          </Stack>
        </Stack>
        <Typography component="span" sx={{ fontSize: 11 }}>
          {last ? formatMonthLabel(last.monthKey) : ""}
        </Typography>
      </Stack>
    </Stack>
  );
}

export default UnitsSparkline;
